import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errText, useAuth } from "@/lib/auth";
import { Sigil } from "@/components/temple/Sigil";

const field = "w-full bg-transparent border-b border-[#D9B47A]/30 py-4 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors";
const btn = "mt-10 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] py-4 font-tt-mono text-[12px] uppercase tracking-[0.25em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60";

const Shell = ({ title, children, testId }) => (
  <main data-testid={testId} className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16">
    <div className="w-full max-w-md">
      <Sigil animate className="w-14 h-14" width={2.5} />
      <h1 className="font-display text-5xl mt-8 leading-[1.05]">{title}</h1>
      {children}
    </div>
  </main>
);

export function SignIn() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setMsg("If an initiate account exists for this email, a reset link is on its way.");
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        navigate(data?.role === "admin" ? "/inbox" : data?.initiate ? "/gates/root-resonance" : "/initiate");
      }
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testId="signin-page" title={mode === "forgot" ? <>Find your <em className="text-[#D9B47A]">way back</em></> : <>Initiate <em className="text-[#D9B47A]">sign in</em></>}>
      <form onSubmit={submit}>
        <input data-testid="signin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={`${field} mt-10`} />
        {mode === "signin" && <input data-testid="signin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={`${field} mt-4`} />}
        {error && <p data-testid="signin-error" className="mt-4 text-sm text-[#E8838E]">{error}</p>}
        {msg && <p data-testid="signin-message" className="mt-4 text-sm text-[#D9B47A]">{msg}</p>}
        <button data-testid="signin-submit" disabled={busy} className={btn}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{mode === "forgot" ? "Send reset link" : "Enter"}</button>
      </form>
      <button data-testid="signin-toggle-forgot" onClick={() => { setMode(mode === "forgot" ? "signin" : "forgot"); setMsg(""); setError(""); }} className="mt-6 text-sm text-[#CFC2B6] underline hover:text-[#D9B47A]">
        {mode === "forgot" ? "Back to sign in" : "Forgot your password?"}
      </button>
      <Link to="/initiate" className="block mt-3 text-sm text-[#A8998C] hover:text-[#D9B47A]">Not yet an initiate? Become one</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [state, setState] = useState("form");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token") ?? "", password });
      setState("done");
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    }
  };
  return (
    <Shell testId="reset-page" title={<>A new <em className="text-[#D9B47A]">key</em></>}>
      {state === "done" ? (
        <>
          <p data-testid="reset-done" className="mt-6 text-[#CFC2B6]">Your password has been changed.</p>
          <Link to="/signin" className={btn}>Sign in</Link>
        </>
      ) : (
        <form onSubmit={submit}>
          <input data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" className={`${field} mt-10`} />
          {error && <p data-testid="reset-error" className="mt-4 text-sm text-[#E8838E]">{error}</p>}
          <button data-testid="reset-submit" className={btn}>Save new password</button>
        </form>
      )}
    </Shell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [product, setProduct] = useState(["quantum", "bundle"].includes(params.get("product")) ? params.get("product") : "gates");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        if (data?.product) setProduct(data.product);
        if (data?.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (data?.status === "expired") {
          setState("expired");
          return;
        }
      } catch {
        if (!alive) return;
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [params, refresh]);

  const copy = {
    checking: ["Receiving your offering…", "Please stay a moment while we confirm your payment."],
    paid: product === "bundle" ? ["Welcome, initiate.", "Both apps are now yours: 13 Gates to Ecstasy and Quantum Intimacy."] : product === "quantum" ? ["Welcome to Quantum Intimacy.", "Every chapter of Book II is now open to you. Step beyond the flesh."] : ["Welcome, initiate.", "All 13 Mastery Guide rituals are now open to you. Begin at the root."],
    expired: ["The session closed.", "No payment was taken. You can begin again whenever you are ready."],
    timeout: ["Still confirming.", "Your payment is being processed. Refresh this page in a minute, or sign in later — your rituals open as soon as it completes."],
    error: ["Something went astray.", "We could not find this payment session."],
  }[state];

  return (
    <Shell testId="payment-success-page" title={copy[0]}>
      <p data-testid="payment-state" data-state={state} className="mt-6 text-lg text-[#CFC2B6]">{copy[1]}</p>
      {state === "checking" && <Loader2 className="mt-8 w-6 h-6 animate-spin text-[#D9B47A]" />}
      {state === "paid" && product === "bundle" && <Link to="/apps" data-testid="payment-begin" className={btn}>Open your apps</Link>}
      {state === "paid" && product === "quantum" && <Link to="/quantum" data-testid="payment-begin" className={btn}>Open Quantum Intimacy</Link>}
      {state === "paid" && product === "gates" && <Link to="/gates/root-resonance" data-testid="payment-begin" className={btn}>Enter Gate 01 · Root Resonance</Link>}
      {state === "paid" && product === "gates" && <Link to="/app" data-testid="payment-get-app" className="mt-5 block text-sm text-[#D9B47A] underline underline-offset-4">Or open the 13 Gates to Ecstasy app on your phone</Link>}
      {(state === "expired" || state === "error") && <Link to={`/initiate?product=${product}`} className={btn}>Return</Link>}
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const product = ["quantum", "bundle"].includes(params.get("product")) ? params.get("product") : "gates";
  return (
    <Shell testId="payment-cancel-page" title={<>No offering <em className="text-[#D9B47A]">taken.</em></>}>
      <p className="mt-6 text-lg text-[#CFC2B6]">Your payment was cancelled and nothing was charged. The temple remains open whenever you are ready.</p>
      <Link to={`/initiate?product=${product}`} data-testid="payment-retry" className={btn}>Return to initiation</Link>
    </Shell>
  );
}
